// ============================================
// Updated AddressField.js using the utils

import React, { useState, useEffect, useRef } from 'react';
import {
  loadGoogleMapsScript,
  createAutocomplete,
  cleanupAutocomplete,
  formatAddressForDisplay
} from '../../utils/addressUtils';

const AddressField = ({ field, value, error, onChange }) => {
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [loading, setLoading] = useState(true);
  const addressInputRef = useRef(null);

  // Load Google Maps API script
  useEffect(() => {
    const loadMaps = async () => {
      try {
        await loadGoogleMapsScript();
        setGoogleMapsLoaded(true);
      } catch (error) {
        console.error('Failed to load Google Maps:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMaps();
  }, []);

  // Initialize autocomplete when maps are loaded
  useEffect(() => {
    if (googleMapsLoaded && addressInputRef.current && !autocomplete) {
      try {
        const autocompleteInstance = createAutocomplete(
          addressInputRef.current,
          (addressData) => {
            onChange(addressData);
          }
        );
        
        setAutocomplete(autocompleteInstance);
      } catch (error) {
        console.error('Error creating autocomplete:', error);
      }
    }
  }, [googleMapsLoaded, onChange, autocomplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autocomplete) {
        cleanupAutocomplete(autocomplete);
      }
    };
  }, [autocomplete]);

  const displayValue = formatAddressForDisplay(value);

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {field.title}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        ref={addressInputRef}
        type="text"
        value={displayValue}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
        placeholder="Enter your address"
        disabled={loading}
      />
      {loading && (
        <div className="text-xs text-gray-500 mt-1">Loading Google Maps...</div>
      )}
      {!loading && !googleMapsLoaded && (
        <div className="text-xs text-red-500 mt-1">Failed to load Google Maps</div>
      )}
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
    </div>
  );
};

export default AddressField;